import { apiClient } from '@/auth';

export type ClinicResponse = {
  id: number;
  name: string;
  hospital_id?: number;
  hospital_name?: string;
  flags?: Record<string, unknown>;
};

export type HospitalResponse = {
  id: number;
  name: string;
  full_name?: string | null;
};

/** `ClinicInvitationCodeResponse` per swagger:15487. The `invitation_code`
 *  is the human-friendly string a clinician hands a patient to join the
 *  clinic during sign-up. */
export type ClinicInvitationCodeResponse = {
  id: number;
  clinic_id: number;
  invitation_code: string;
  created_time: string;
  updated_time: string;
};

/** Body for POST `/hospitals/{hospital_id}/patients/{patient_id}/move_clinic`
 *  (FOOD-651). Omit `move` to retarget every supported type; `[]` = membership only. */
export type MovePatientClinicInput = {
  from_clinic_id: number;
  to_clinic_id: number;
  move?: Array<'reports' | 'subscriptions'>;
};

export const hospitalApi = {
  /**
   * Every clinic the clinician is registered in, across every hospital they
   * belong to — not just their home hospital. GET /clinicians/{clinician_id}/clinics
   * (FOOD-837). Each clinic carries its own `hospital_id` and `hospital_name`.
   */
  async allClinics(clinicianId: number): Promise<ClinicResponse[]> {
    const res = await apiClient.get<ClinicResponse[]>(`/clinicians/${clinicianId}/clinics`);
    return Array.isArray(res.data) ? res.data : [];
  },

  /**
   * Hospital details (name, etc.). Used to label clinic-filter groups.
   * GET /hospitals/{hospital_id} — MNT swagger:6517.
   */
  async get(hospitalId: number): Promise<HospitalResponse> {
    const res = await apiClient.get<HospitalResponse>(`/hospitals/${hospitalId}`);
    return res.data;
  },

  /**
   * Resend the hospital-invitation email to a patient. Drives the envelope
   * flag-icon on the patient list. POST `/hospitals/{hospital_id}/send_hospital_invitation_email`
   * (MNT swagger:7208) — `invitation_id` is optional and only set when the
   * row carries one.
   */
  async resendInvitationEmail(args: {
    hospitalId: number;
    patientId: number;
    invitationId?: number | null;
  }): Promise<void> {
    await apiClient.post(
      `/hospitals/${args.hospitalId}/send_hospital_invitation_email`,
      undefined,
      {
        params: {
          patient_id: args.patientId,
          invitation_id: args.invitationId ?? undefined,
        },
      },
    );
  },

  /**
   * Deregister a patient from a clinic — patient-list "delete" flag-icon.
   * DELETE `/hospitals/{hospital_id}/clinics/{clinic_id}/deregister_patient`
   * (MNT swagger:6835).
   */
  async deregisterPatient(args: {
    hospitalId: number;
    clinicId: number;
    patientId: number;
  }): Promise<void> {
    await apiClient.delete(
      `/hospitals/${args.hospitalId}/clinics/${args.clinicId}/deregister_patient`,
      { params: { patient_id: args.patientId } },
    );
  },

  /**
   * Update a clinic invitation's status — used to revoke a pending
   * invitation from the patient list. PUT `/clinic-invitation/{invitation_id}?status=revoked`
   * (MNT swagger:4761). Status enum:
   *   accepted | rejected | pending | expired | revoked
   */
  async updateClinicInvitation(args: {
    invitationId: number;
    status: 'accepted' | 'rejected' | 'pending' | 'expired' | 'revoked';
  }): Promise<void> {
    await apiClient.put(`/clinic-invitation/${args.invitationId}`, undefined, {
      params: { status: args.status },
    });
  },

  /**
   * The clinic's default invitation code (shareable with new patients).
   * GET /hospitals/{hospital_id}/clinics/{clinic_id}/invitation-codes/default
   * Per MNT swagger:6678.
   */
  async clinicInvitationCode(args: {
    hospitalId: number;
    clinicId: number;
  }): Promise<ClinicInvitationCodeResponse> {
    const res = await apiClient.get<ClinicInvitationCodeResponse>(
      `/hospitals/${args.hospitalId}/clinics/${args.clinicId}/invitation-codes/default`,
    );
    return res.data;
  },

  /**
   * Move a patient from one clinic to another in the same hospital (FOOD-651).
   * POST `/hospitals/{hospital_id}/patients/{patient_id}/move_clinic`.
   * Does not deactivate incomplete logging periods (unlike deregister).
   */
  async movePatientClinic(args: {
    hospitalId: number;
    patientId: number;
    body: MovePatientClinicInput;
  }): Promise<void> {
    await apiClient.post(
      `/hospitals/${args.hospitalId}/patients/${args.patientId}/move_clinic`,
      args.body,
    );
  },
};
